import React from 'react';
import {
    Grid,
    Box,
    Typography,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    IconButton,
    Chip,
} from "@mui/material";
import {
    Business as BuildingIcon,
    Email as MailIcon,
    Phone as PhoneIcon,
    Language as GlobeIcon,
    ContentCopy as CopyIcon
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import _ from "lodash";
import { colors, typography } from "../../styles/globals.js";
import ContactRow from './ContactRow.js';
import { formatBusinessName, formatAddress, formatPhoneNumber } from '../../utils/nameFormatters';
import { isInactive } from '../../views/OwnershipNetworkView';

const PRIMARY = "#4566EB";       // button/outline
const OUTLINE  = "#C8D1EA";      // default outline
const LABEL    = "#0D1B3F";      // text
const FILL     = "#F6F8FC";      // selected bg

const StateRadio = styled(Radio)(({ theme }) => ({
    padding: 0,
    marginRight: 12,
    "& .MuiSvgIcon-root": { fontSize: 22 },
    color: OUTLINE,
    "&.Mui-checked": { color: PRIMARY },
    // make the radio look like the comp (thicker ring)
    "& .MuiSvgIcon-root circle": { strokeWidth: 6 },
}));

const StateChip = styled(FormControlLabel)(({ theme }) => ({
    margin: 4,
    padding: "8px 12px",
    borderRadius: 12,
    border: `1px solid ${OUTLINE}`,
    background: "#fff",
    transition: "border-color .15s ease, box-shadow .15s ease, background .15s ease",
    color: LABEL,
    fontWeight: 400,
    letterSpacing: "0.02em",
    lineHeight: 1,
    // hover ring
    "&:hover": {
        borderColor: PRIMARY,
        boxShadow: `0 0 0 4px ${alpha(PRIMARY, 0.08)}`,
    },
    // selected styles
    "&.MuiFormControlLabel-root.Mui-checked, & .Mui-checked ~ span": {
        // label color stays dark; container changes via parent class below
    },
    // when the Radio is checked, MUI adds .Mui-checked on the <input>, so we style the container via sibling selector
    "& .Mui-checked": {}, // noop: keeps selector chain available
    "& .MuiRadio-root.Mui-checked + .MuiFormControlLabel-label": {
        // keeps label bold & dark
    },
    // container "selected" look (use has() to detect checked input when supported; fallback via class on wrapper below)
    "&.isSelected": {
        borderColor: PRIMARY,
        background: FILL,
    },
    "& .MuiFormControlLabel-label": {
        fontSize: 14,
        fontWeight: 400,
    },
}));

// Styles from BusinessProfileView
const styles = {
    contactContainer: {
        padding: '24px',
        backgroundColor: '#F6F8FC',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: "column",
        gap: '16px',
        maxWidth: 600,
        marginTop: "10px"
    },
    businessNameHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        mb: 2
    },
    businessName: {
        fontSize: typography.fontSizes.h3,
        fontWeight: typography.fontWeights.regular
    },
    contactIcon: {
        fontSize: '16px',
        color: '#6b7280',
    },
    businessDetailsContainer: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        height: 'fit-content',
        marginTop: "10px"
    },
    fieldLabel: {
        fontSize: typography.fontSizes.small,
        fontWeight: typography.fontWeights.regular,
        color: colors.neutral
    },
    fieldValue: {
        fontSize: typography.fontSizes.base,
        fontWeight: typography.fontWeights.regular,
        color: colors.primary
    },
    statusChip: (status) => ({
        backgroundColor: isInactive({type: 'COMPANY', status}) ? '#fee2e2' : '#e8f5e8',
        color: isInactive({type: 'COMPANY', status}) ? '#dc2626' : colors.success,
        fontSize: typography.fontSizes.small,
        fontWeight: typography.fontWeights.medium,
        height: '24px',
        borderRadius: '12px'
    }),
    copyButton: {
        padding: '2px',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
    }
};

const StateSelector = ({
    selectedState,
    onStateChange,
    states = []
}) => {
    return (
        <Box sx={{ mt:0 }}>
            <Typography sx={{ ...styles.fieldLabel, fontSize: 16, fontWeight: 400, ml: 0.5 }}>
                State:
            </Typography>
            <RadioGroup
                row
                name="state-selection"
                value={selectedState}
                onChange={onStateChange}
                sx={{ display: "flex", gap: 0, flexWrap: "wrap", mt: 1 }}
            >
                {states.map((code) => (
                    <StateChip
                        key={code}
                        value={code}
                        control={<StateRadio />}
                        label={code}
                        className={selectedState === code ? "isSelected" : ""}
                    />
                ))}
            </RadioGroup>
        </Box>
    );
};

const OverviewTab = ({
    businessData,
    selectedState,
    onStateChange,
    availableStates = []
}) => {
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        // <Paper sx={{ p: 2 }}>
        <div>
            <Grid container spacing={{ xs: 0.5, sm: 2, md: 3 }}>
                {/* Contact Information Column */}
                <Grid item xs={12} sm={12} md={12} lg={5}>
                    <Box sx={styles.contactContainer}>
                        <Box sx={styles.businessNameHeader}>
                            <BuildingIcon sx={{ fontSize: '25px', color: '#4566EB' }} />
                            <Typography sx={{ ...styles.businessName }}>{formatBusinessName(businessData.businessName)}</Typography>
                        </Box>

                        <ContactRow
                            value={_.toLower(businessData.email) || <Typography sx={{ color: colors.lightGrey }}>No email found</Typography>}
                            icon={<MailIcon sx={styles.contactIcon} />}
                            isInput={businessData.isInputEmail}
                        />
                        <Divider sx={{ width: '100%', color: '#E0E2F1' }} />
                        <ContactRow
                            value={formatPhoneNumber(businessData.phone) || <Typography sx={{ color: colors.lightGrey }}>No phone number found</Typography>}
                            icon={<PhoneIcon sx={styles.contactIcon} />}
                            isInput={businessData.isInputPhone}
                        />
                        <Divider sx={{ width: '100%', color: '#E0E2F1' }} />
                        <ContactRow
                            value={businessData.website || <Typography sx={{ color: colors.lightGrey }}>No website found</Typography>}
                            icon={<GlobeIcon sx={styles.contactIcon} />}
                            hasRisk={false}
                            buttonName='Open link'
                            disabled={!businessData.website}
                            isInput={businessData.isInputWebsite}
                        />
                        <Divider sx={{ width: '100%', color: '#E0E2F1' }} />

                        <StateSelector
                            selectedState={selectedState}
                            onStateChange={onStateChange}
                            states={availableStates}
                        />
                    </Box>
                </Grid>

                {/* Business Details Column 1 */}
                <Grid item xs={12} sm={6} md={6} lg={3.5}>
                    <Box sx={styles.businessDetailsContainer}>
                        <Box>
                            <Typography sx={styles.fieldLabel}>Business name</Typography>
                            <Typography sx={styles.fieldValue}>
                                {formatBusinessName(businessData.businessName) || <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>State ID</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography sx={styles.fieldValue}>
                                    {businessData.stateId || <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>}
                                </Typography>
                                {businessData.stateId && (
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(businessData.stateId)}
                                        sx={styles.copyButton}
                                    >
                                        <CopyIcon sx={{ fontSize: '12px' }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>Incorporation Date</Typography>
                            <Typography sx={styles.fieldValue}>
                                {businessData.incorporationDate ?
                                    new Date(businessData.incorporationDate).toLocaleDateString() :
                                    <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>
                                }
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>EIN</Typography>
                            <Typography sx={styles.fieldValue}>
                                {(businessData.ein && businessData.ein !== "None") ? businessData.ein : <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>Primary address</Typography>
                            <Typography sx={styles.fieldValue}>
                                {formatAddress(businessData.primaryAddress) || <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Business Details Column 2 */}
                <Grid item xs={12} sm={6} md={6} lg={3.5}>
                    <Box sx={styles.businessDetailsContainer}>
                        <Box>
                            <Typography sx={styles.fieldLabel}>Status</Typography>
                            {businessData.status ? (
                                <Chip
                                    label={businessData.status}
                                    sx={styles.statusChip(businessData.status)}
                                />
                            ) : (
                                <Typography sx={{ color: colors.lightGrey }}>N/A</Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>Registered agent</Typography>
                            <Typography sx={styles.fieldValue}>
                                {(businessData.registeredAgent && businessData.registeredAgent !== "None") ?
                                    _.startCase(_.toLower(businessData.registeredAgent)) :
                                    <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>
                                }
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>URL</Typography>
                            <Typography sx={styles.fieldValue}>
                                {(businessData.url && !businessData.url.includes('opencorporates')) ?
                                    businessData.url :
                                    <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>
                                }
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={styles.fieldLabel}>DBA</Typography>
                            <Typography sx={styles.fieldValue}>
                                {businessData.dba ? formatBusinessName(businessData.dba) : <Typography component="span" sx={{ color: colors.lightGrey }}>N/A</Typography>}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
};

export default OverviewTab;